import { useState, useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useRecaptcha } from '@/hooks/useRecaptcha'
import { verifyApi, loginApi } from '@/api/auth.api'
import { AuthErrorCode } from '@/types/auth.types'
import { HumanCaptchaModal } from '@/components/HumanCaptchaModal'

export function LoginOtp() {
  const location = useLocation()
  const { signInWithToken } = useAuth()

  const phoneNumber = location.state?.phoneNumber as string | undefined
  if (!phoneNumber) {
    return <Navigate to="/login" replace />
  }

  return <OtpForm phoneNumber={phoneNumber} signInWithToken={signInWithToken} />
}

function OtpForm({
  phoneNumber,
  signInWithToken,
}: {
  phoneNumber: string
  signInWithToken: (token: string) => Promise<void>
}) {
  const navigate = useNavigate()
  const [verificationCode, setVerificationCode] = useState<string[]>(() => Array(6).fill(''))
  const [isVerifying, setIsVerifying] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isResending, setIsResending] = useState(false)
  const [resendTimer, setResendTimer] = useState(20)
  const [canResend, setCanResend] = useState(false)
  const firstInputRef = useRef<HTMLInputElement>(null)

  // Human reCAPTCHA state
  const [requiresHumanCaptcha, setRequiresHumanCaptcha] = useState(false)
  const [humanCaptchaSiteKey, setHumanCaptchaSiteKey] = useState<string | null>(null)
  const [captchaError, setCaptchaError] = useState<string | null>(null)
  const [pendingHumanCaptcha, setPendingHumanCaptcha] = useState(false)

  // Resend timer
  useEffect(() => {
    if (resendTimer <= 0) {
      setCanResend(true)
      return
    }
    const timer = setInterval(() => setResendTimer((prev) => prev - 1), 1000)
    return () => clearInterval(timer)
  }, [resendTimer])

  // Auto-focus first input
  useEffect(() => {
    const timer = setTimeout(() => firstInputRef.current?.focus(), 300)
    return () => clearTimeout(timer)
  }, [])

  const clearVerificationCode = useCallback(() => {
    setVerificationCode(Array(6).fill(''))
    requestAnimationFrame(() => firstInputRef.current?.focus())
  }, [])

  const handleSuccessfulVerification = useCallback(
    async (token: string) => {
      setIsVerified(true)
      await signInWithToken(token)
      navigate('/dashboard', { replace: true })
    },
    [signInWithToken, navigate]
  )

  const handleHumanCaptchaComplete = useCallback(
    async (token: string) => {
      if (!phoneNumber || !token || isVerifying) return

      setIsVerifying(true)
      setErrorMessage(null)

      try {
        const response = await verifyApi({
          phoneNumber,
          verificationCode: verificationCode.join(''),
          recaptchaToken: token,
          isHumanCaptcha: true,
        })

        if (response.success && response.token) {
          setRequiresHumanCaptcha(false)
          setHumanCaptchaSiteKey(null)
          await handleSuccessfulVerification(response.token)
        } else if (response.errorCode === AuthErrorCode.RateLimitExceeded) {
          setErrorMessage(response.errorMessage || 'Too many attempts. Please try again later')
          setRequiresHumanCaptcha(false)
          setHumanCaptchaSiteKey(null)
          setCaptchaError(null)
          setCanResend(false)
        } else if (response.errorCode === AuthErrorCode.RecaptchaFailed) {
          setCaptchaError('Security check failed. Please try again.')
        } else {
          setErrorMessage(response.errorMessage || 'Invalid or expired code')
          clearVerificationCode()
          setIsVerifying(false)
          setPendingHumanCaptcha(false)
        }
      } catch {
        setErrorMessage('Failed to complete verification')
        clearVerificationCode()
        setIsVerifying(false)
        setPendingHumanCaptcha(false)
      }
    },
    [phoneNumber, isVerifying, verificationCode, handleSuccessfulVerification, clearVerificationCode]
  )

  const { getToken, resetRecaptcha, handleBackFromHumanCaptcha } = useRecaptcha({
    requiresHumanCaptcha,
    humanCaptchaSiteKey,
    onHumanCaptchaComplete: handleHumanCaptchaComplete,
  })

  const handleCodeSubmit = useCallback(
    async (codeToSubmit?: string[]) => {
      if (isVerifying || isVerified) return

      setErrorMessage(null)
      setIsVerifying(true)

      try {
        const recaptchaToken = await getToken('loginVerify')
        if (!recaptchaToken) {
          throw new Error('Failed to get security token')
        }

        const codeToUse = codeToSubmit || verificationCode
        const response = await verifyApi({
          phoneNumber,
          verificationCode: codeToUse.join(''),
          recaptchaToken,
        })

        if (response.requiresHumanCaptcha && response.humanCaptchaSiteKey) {
          setRequiresHumanCaptcha(true)
          setHumanCaptchaSiteKey(response.humanCaptchaSiteKey)
          setPendingHumanCaptcha(true)
          setIsVerifying(false)
          return
        }

        if (response.success && response.token) {
          await handleSuccessfulVerification(response.token)
        } else {
          if (response.errorCode === AuthErrorCode.RateLimitExceeded) {
            setErrorMessage('Too many attempts. Please try again later')
            setCanResend(false)
          } else if (response.errorCode === AuthErrorCode.InvalidVerificationCode) {
            setErrorMessage('Invalid or expired code')
          } else {
            setErrorMessage(response.errorMessage || 'Invalid or expired code')
          }
          clearVerificationCode()
          setPendingHumanCaptcha(false)
          setIsVerifying(false)
        }
      } catch {
        setErrorMessage('Failed to verify code')
        clearVerificationCode()
        setPendingHumanCaptcha(false)
        setIsVerifying(false)
      }
    },
    [verificationCode, isVerified, phoneNumber, getToken, isVerifying, handleSuccessfulVerification, clearVerificationCode]
  )

  const checkAndSubmitCode = useCallback(
    async (code: string[]) => {
      if (
        code.length === 6 &&
        code.every((digit) => digit !== '') &&
        !isVerifying &&
        !pendingHumanCaptcha
      ) {
        await handleCodeSubmit(code)
      }
    },
    [isVerifying, pendingHumanCaptcha, handleCodeSubmit]
  )

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value
    const digit = value.length > 0 ? value.slice(-1) : ''

    const newCode = [...verificationCode]
    newCode[index] = digit
    setVerificationCode(newCode)
    setErrorMessage(null)

    if (digit && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`) as HTMLInputElement
      nextInput?.focus()
    }

    if (digit) {
      checkAndSubmitCode(newCode)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if ((e.key === 'Backspace' || e.key === 'Delete') && index >= 0) {
      if (verificationCode[index]) {
        const newCode = [...verificationCode]
        newCode[index] = ''
        setVerificationCode(newCode)
      } else if (index > 0) {
        const prevInput = document.getElementById(`code-${index - 1}`) as HTMLInputElement
        prevInput?.focus()
      }
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData('text').trim()
    if (/^\d+$/.test(pasteData)) {
      const digits = pasteData.split('').slice(0, 6)
      const newCode = [...digits, ...Array(6 - digits.length).fill('')].slice(0, 6)
      setVerificationCode(newCode)

      const targetIndex = Math.min(digits.length - 1, 5)
      const targetInput = document.getElementById(`code-${targetIndex}`) as HTMLInputElement
      targetInput?.focus()

      if (digits.length === 6) {
        checkAndSubmitCode(newCode)
      }
      e.preventDefault()
    }
  }

  const handleResendCode = async () => {
    if (!canResend || !phoneNumber) return
    setIsResending(true)
    setErrorMessage(null)

    try {
      const recaptchaToken = await getToken('login')
      if (!recaptchaToken) {
        setErrorMessage('Failed to resend code')
        return
      }

      const response = await loginApi({ phoneNumber, recaptchaToken })

      if (response.requiresHumanCaptcha && response.humanCaptchaSiteKey) {
        setRequiresHumanCaptcha(true)
        setHumanCaptchaSiteKey(response.humanCaptchaSiteKey)
        setIsResending(false)
        return
      }

      if (response.success) {
        setResendTimer(60)
        setCanResend(false)
        clearVerificationCode()
      } else {
        setErrorMessage(response.errorMessage || 'Failed to resend code')
      }
    } catch {
      setErrorMessage('Failed to resend code')
    } finally {
      setIsResending(false)
    }
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleCodeSubmit()
  }

  const handleBackFromHumanCaptchaLocal = () => {
    handleBackFromHumanCaptcha(
      setRequiresHumanCaptcha,
      setHumanCaptchaSiteKey,
      setCaptchaError
    )
  }

  const handleRetryHumanCaptchaLocal = () => {
    resetRecaptcha(setCaptchaError)
  }

  return (
    <>
      <div className="flex min-h-screen flex-col items-center justify-center px-6">
        <div className="w-full max-w-sm space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold">Verify OTP</h1>
            <p className="mt-2 text-muted-foreground">
              Enter the code sent to {phoneNumber}
            </p>
          </div>

          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div className="flex justify-between gap-2">
              {[...Array(6)].map((_, index) => (
                <input
                  key={index}
                  ref={index === 0 ? firstInputRef : undefined}
                  id={`code-${index}`}
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  aria-label={`Digit ${index + 1} of verification code`}
                  className={`h-14 w-full rounded-xl border text-center text-2xl font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                    errorMessage
                      ? 'border-destructive bg-destructive/10'
                      : verificationCode[index]
                        ? 'border-primary/30 bg-primary/5'
                        : 'border-border bg-accent'
                  }`}
                  value={verificationCode[index] || ''}
                  onChange={(e) => handleCodeChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  onFocus={(e) => e.target.value && e.target.select()}
                />
              ))}
            </div>

            {errorMessage && (
              <p className="text-center text-sm text-destructive">{errorMessage}</p>
            )}

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Didn't get the code?</span>
              {canResend ? (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isResending}
                  className="font-medium text-primary hover:text-primary/80 disabled:opacity-50"
                >
                  {isResending ? 'Sending...' : 'Resend Code'}
                </button>
              ) : (
                <span className="text-muted-foreground">Resend in {resendTimer}s</span>
              )}
            </div>
          </form>

          {isVerifying && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          )}
        </div>
      </div>

      {requiresHumanCaptcha && humanCaptchaSiteKey ? (
        <HumanCaptchaModal
          siteKey={humanCaptchaSiteKey}
          actionText="verify"
          onBack={handleBackFromHumanCaptchaLocal}
          onRetry={handleRetryHumanCaptchaLocal}
          showRetry={!!captchaError}
          errorMessage={captchaError || undefined}
          isSubmitting={isVerifying}
        />
      ) : null}
    </>
  )
}
